/**
 * format.js — display formatting. Nothing here does arithmetic.
 *
 * v1 formatted money inconsistently: some cells passed
 * `{ minimumFractionDigits: 2, maximumFractionDigits: 2 }`, its neighbour
 * omitted the maximum and defaulted to three decimals, and several places
 * called `.toFixed(2)` and then did arithmetic on the resulting string.
 */

import { toRupees } from './money';
import { ToWords } from 'to-words';

const inrFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const plainFormatter = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});


/** Integer paise -> "₹1,23,456.78" (Indian digit grouping). */
export function formatMoney(paise, { symbol = true } = {}) {
  const rupees = toRupees(paise);
  return symbol ? inrFormatter.format(rupees) : plainFormatter.format(rupees);
}


const qtyFormatter = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 3 });

export function formatQuantity(qty, unit) {
  const n = Number(qty);
  const text = Number.isFinite(n) ? qtyFormatter.format(n) : '0';
  return unit ? `${text} ${unit}` : text;
}

export function formatPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0%';
  return `${Number(n.toFixed(3))}%`;
}

const toWords = new ToWords({
  localeCode: 'en-IN',
  converterOptions: { currency: true, ignoreDecimal: false, ignoreZeroCurrency: false },
});

/**
 * Amount in words, as required on a tax invoice.
 * v1 passed an unrounded float straight to to-words, so the words could carry
 * spurious paise that did not match the printed figure.
 */
export function amountInWords(paise) {
  try {
    return toWords.convert(toRupees(paise));
  } catch {
    return '';
  }
}

/* ------------------------------------------------------------------ *
 * Dates
 * ------------------------------------------------------------------ */

/** Accepts a Date, a Firestore Timestamp, epoch ms, or an ISO string. */
export function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object' && typeof value.toDate === 'function') return value.toDate();
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000);
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const dateFormatter = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const dateTimeFormatter = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export function formatDate(value, fallback = '—') {
  const d = toDate(value);
  return d ? dateFormatter.format(d) : fallback;
}

export function formatDateTime(value, fallback = '—') {
  const d = toDate(value);
  return d ? dateTimeFormatter.format(d) : fallback;
}

/** yyyy-MM-dd for <input type="date">. */
export function toDateInputValue(value) {
  const d = toDate(value);
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function fromDateInputValue(text) {
  if (!text) return null;
  const [y, m, d] = String(text).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}

/** "3 days overdue" / "due in 5 days" / "due today". */
export function describeDueDate(dueDate, reference = new Date()) {
  const d = toDate(dueDate);
  if (!d) return null;
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(d) - startOf(reference)) / 86400000);
  if (days === 0) return { text: 'Due today', tone: 'warning', days };
  if (days < 0) return { text: `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} overdue`, tone: 'danger', days };
  return { text: `Due in ${days} day${days === 1 ? '' : 's'}`, tone: 'neutral', days };
}

export function initials(name) {
  return String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?';
}

export function truncate(text, max = 60) {
  const t = String(text || '');
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}
